// client/src/components/secure/training/Reports.js — the managing broker's compliance view:
// completion by course and by person, the overdue list, CE standing for every licence,
// CSV export, each person's printable record, and training profiles. "Open" on a person shows
// their whole training (automatic and by hand) with Assign courses…, due dates, cancel / waive.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../../hooks/useApiClient';
import CeTracker from './CeTracker';
import { PersonTraining } from './Assign';
import { Btn, Field, Modal, Notice, Pill, ROLE_LABEL, fetchBlob, fmtDate, inputCls, openBlob } from './shared';
import DataList from '../../../components/shared/DataList';

export const CeOverview = () => {
    const api = useApiClient();
    const [d, setD] = useState(null);
    const [error, setError] = useState('');
    const [person, setPerson] = useState(null);
    const load = useCallback(async () => {
        try { setD(await api.get('/api/training/ce-overview')); setError(''); } catch (e) { setError(e.message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);
    if (!d) return error ? <Notice tone="bad">{error}</Notice> : <p>Loading…</p>;
    return (
        <div className="space-y-4">
            <Notice>{d.disclaimer}</Notice>
            <section className="bg-white rounded-lg shadow p-4 overflow-x-auto">
                <h3 className="font-semibold text-slate-800 mb-2">Every licence, most urgent first</h3>
                <DataList rows={d.licences} rowKey="licence_id" csvName="licence-standing" defaultSort={{ key: 'cycle_end', dir: 'asc' }}
                    filters={[{ key: 'level', label: 'Standing' }]}
                    columns={[
                        { key: 'name', label: 'Person' },
                        { key: 'licence', label: 'Licence', value: (l) => `${l.state} ${String(l.licence_type).replace(/_/g, ' ')}${l.licence_number ? ` · ${l.licence_number}` : ''}${Number(l.is_example) === 1 ? ' (example)' : ''}` },
                        { key: 'cycle_end', label: 'Expires', type: 'date', render: (l) => <>{fmtDate(l.cycle_end)}{l.days_left != null ? <div className="text-xs text-slate-500">{l.days_left} d</div> : null}</> },
                        { key: 'hours', label: 'Hours', value: (l) => `${l.done ?? '—'} / ${l.required ?? '—'}`, sortValue: (l) => (l.done == null ? null : Number(l.done)) },
                        { key: 'level', label: 'Standing', value: (l) => `${l.level}${l.message ? ` — ${l.message}` : ''}`, sortValue: (l) => l.level,
                            render: (l) => <><Pill s={l.level} /><div className="text-xs text-slate-600 max-w-sm">{l.message}</div></> },
                        { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (l) => <button type="button" className="text-indigo-700 underline" onClick={() => setPerson(l)}>Open</button> },
                    ]} />
                {!d.licences.length && <p className="text-sm text-slate-600">No licences recorded yet.</p>}
            </section>
            {d.agents_without_licence_record.length > 0 && (
                <Notice tone="warn">No licence recorded in the CE tracker for: {d.agents_without_licence_record.map((p) => p.name).join(', ')}. Open the person under People to add it (or ask them to add it under Training → My CE).</Notice>
            )}
            {person && <Modal title={`CE — ${person.name}`} onClose={() => { setPerson(null); load(); }} wide><CeTracker personType={person.person_type} personId={person.person_id} canVerify /></Modal>}
        </div>
    );
};

const ProfileEditor = ({ p, onClose, onSaved, canEdit }) => {
    const api = useApiClient();
    const [f, setF] = useState({ roles: p.profile.roles.join(','), states: p.profile.states.join(','), works_in_chicago: p.profile.works_in_chicago, is_supervisor: p.profile.is_supervisor, exempt: p.profile.exempt });
    const [error, setError] = useState('');
    const save = async () => {
        try { await api.put(`/api/training/people/${p.person_type}/${p.person_id}/profile`, f); onSaved(); } catch (e) { setError(e.message); }
    };
    return (
        <Modal title={`Training profile — ${p.name}`} onClose={onClose}>
            {error && <Notice tone="bad">{error}</Notice>}
            <p className="text-sm text-slate-600 mb-3">The rules use this to decide what {p.name} owes. It starts from their record (agent, managing broker, office staff, PM staff) and the site's state; change it only when that is wrong.</p>
            <Field label="Roles" hint="agent, managing_broker, office_staff, pm_staff"><input className={inputCls} disabled={!canEdit} value={f.roles} onChange={(e) => setF({ ...f, roles: e.target.value })} /></Field>
            <Field label="States worked" hint="IL, FL"><input className={inputCls} disabled={!canEdit} value={f.states} onChange={(e) => setF({ ...f, states: e.target.value })} /></Field>
            {[['works_in_chicago', 'Works in the City of Chicago (Chicago harassment and bystander rules apply)'], ['is_supervisor', 'Supervises employees'], ['exempt', 'Exempt from all training rules (e.g. inactive licence, on leave)']].map(([k, l]) => (
                <label key={k} className="flex gap-2 text-sm mb-2"><input type="checkbox" disabled={!canEdit} checked={!!f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} />{l}</label>
            ))}
            <div className="flex justify-end gap-2 mt-3"><Btn kind="ghost" onClick={onClose}>Close</Btn>{canEdit && <Btn onClick={save}>Save</Btn>}</div>
        </Modal>
    );
};

const Reports = ({ isMB }) => {
    const api = useApiClient();
    const [rep, setRep] = useState(null);
    const [people, setPeople] = useState([]);
    const [overdue, setOverdue] = useState([]);
    const [error, setError] = useState('');
    const [profile, setProfile] = useState(null);
    const [ceFor, setCeFor] = useState(null);
    const [msg, setMsg] = useState('');
    const [openPerson, setOpenPerson] = useState(null);

    const load = useCallback(async () => {
        try {
            const [r, p, o] = await Promise.all([api.get('/api/training/reports/completion'), api.get('/api/training/people'), api.get('/api/training/assignments?status=overdue')]);
            setRep(r); setPeople(p.people); setOverdue(o.assignments); setError('');
        } catch (e) { setError(e.message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const exportCsv = async (status) => {
        try { openBlob(await fetchBlob(`/api/training/reports/export.csv${status ? `?status=${status}` : ''}`), `training-${status || 'all'}.csv`); } catch (e) { setError(e.message); }
    };
    const markDone = async (a) => {
        const note = window.prompt('How was it completed? (kept on the record)');
        if (!note) return;
        try { await api.post(`/api/training/assignments/${a.assignment_id}/complete`, { note }); setMsg('Marked complete.'); load(); } catch (e) { setError(e.message); }
    };
    const waive = async (a) => {
        const reason = window.prompt('Reason for the waiver (kept on the record)');
        if (!reason) return;
        try { await api.post(`/api/training/assignments/${a.assignment_id}/waive`, { reason }); setMsg('Waived.'); load(); } catch (e) { setError(e.message); }
    };

    if (!rep) return error ? <Notice tone="bad">{error}</Notice> : <p>Loading…</p>;
    return (
        <div className="space-y-5">
            {error && <Notice tone="bad">{error}</Notice>}
            {msg && <Notice tone="ok">{msg}</Notice>}
            <div className="flex flex-wrap gap-2 justify-end">
                <Btn kind="ghost" onClick={() => exportCsv('')}>Export all (CSV)</Btn>
                <Btn kind="ghost" onClick={() => exportCsv('overdue')}>Export overdue (CSV)</Btn>
            </div>

            <section className="bg-white rounded-lg shadow p-4 overflow-x-auto">
                <h3 className="font-semibold text-slate-800 mb-2">Overdue ({overdue.length})</h3>
                {!overdue.length ? <p className="text-sm text-slate-600">Nothing overdue.</p> : (
                    <DataList rows={overdue} rowKey="assignment_id" csvName="training-overdue" defaultSort={{ key: 'due_date', dir: 'asc' }}
                        filters={[{ key: 'course_title', label: 'Course' }]}
                        columns={[
                            { key: 'person_name', label: 'Person' },
                            { key: 'course_title', label: 'Course' },
                            { key: 'due_date', label: 'Due', type: 'date', render: (a) => `due ${fmtDate(a.due_date)}` },
                            { key: 'escalated', label: 'Escalated', value: (a) => (a.escalated_at ? 'escalated' : '') },
                            { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (a) => (
                                <span className="space-x-2 whitespace-nowrap">
                                    <button type="button" className="text-indigo-700 underline" onClick={() => setOpenPerson({ person_type: a.person_type, person_id: a.person_id })}>Person</button>
                                    <button type="button" className="text-indigo-700 underline" onClick={() => markDone(a)}>Mark complete…</button>
                                    {isMB && <button type="button" className="text-slate-600 underline" onClick={() => waive(a)}>Waive…</button>}
                                </span>) },
                        ]} />
                )}
            </section>

            <section className="bg-white rounded-lg shadow p-4 overflow-x-auto">
                <h3 className="font-semibold text-slate-800 mb-2">By person</h3>
                <DataList rows={people} rowKey={(p) => `${p.person_type}:${p.person_id}`} csvName="training-people" defaultSort={{ key: 'name', dir: 'asc' }}
                    filters={[{ key: 'person_type', label: 'Type' }]}
                    columns={[
                        { key: 'name', label: 'Person', render: (p) => <>{p.name} <span className="text-xs text-slate-400">({p.person_type})</span></> },
                        { key: 'person_type', label: 'Type' },
                        { key: 'roles', label: 'Roles', value: (p) => `${p.profile.roles.map((r) => ROLE_LABEL[r] || r).join(', ')}${p.profile.works_in_chicago ? ' · Chicago' : ''}${p.profile.exempt ? ' · exempt' : ''}` },
                        { key: 'open', label: 'Open', type: 'number' },
                        { key: 'overdue', label: 'Overdue', type: 'number', render: (p) => (p.overdue ? <Pill s="overdue">{p.overdue}</Pill> : 0) },
                        { key: 'completed', label: 'Completed', type: 'number' },
                        { key: 'licences', label: 'Licences', type: 'number' },
                        { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (p) => (
                            <span className="space-x-2 whitespace-nowrap">
                                <button type="button" className="text-indigo-700 underline font-medium" onClick={() => setOpenPerson(p)}>Open</button>
                                <Link className="text-indigo-700 underline" to={`/dashboard/training/record/${p.person_type}/${p.person_id}`}>Record</Link>
                                <button type="button" className="text-indigo-700 underline" onClick={() => setCeFor(p)}>CE</button>
                                <button type="button" className="text-indigo-700 underline" onClick={() => setProfile(p)}>Profile</button>
                            </span>) },
                    ]} />
            </section>

            <section className="bg-white rounded-lg shadow p-4 overflow-x-auto">
                <h3 className="font-semibold text-slate-800 mb-2">By course</h3>
                <DataList rows={rep.by_course} rowKey="course_id" csvName="training-by-course" defaultSort={{ key: 'title', dir: 'asc' }}
                    columns={[
                        { key: 'title', label: 'Course', render: (c) => <>{c.title} {c.course_status !== 'approved' && <Pill s={c.course_status} />}</> },
                        { key: 'assigned', label: 'Assigned', type: 'number' },
                        { key: 'completed', label: 'Completed', type: 'number', value: (c) => Number(c.completed) || 0 },
                        { key: 'open_n', label: 'Open', type: 'number', value: (c) => Number(c.open_n) || 0 },
                        { key: 'overdue', label: 'Overdue', type: 'number', value: (c) => Number(c.overdue) || 0 },
                        { key: 'avg_score', label: 'Avg quiz', type: 'number', render: (c) => (c.avg_score != null ? `${c.avg_score}%` : '—') },
                    ]} />
            </section>
            {profile && <ProfileEditor p={profile} canEdit={isMB} onClose={() => setProfile(null)} onSaved={() => { setProfile(null); load(); }} />}
            {openPerson && <PersonTraining personType={openPerson.person_type} personId={openPerson.person_id} isMB={isMB} onClose={() => { setOpenPerson(null); load(); }} />}
            {ceFor && <Modal title={`CE — ${ceFor.name}`} onClose={() => { setCeFor(null); load(); }} wide><CeTracker personType={ceFor.person_type} personId={ceFor.person_id} canVerify /></Modal>}
        </div>
    );
};

export default Reports;
